import React from "react";
import {useSelector} from "react-redux";
import {CopyToClipboard} from "react-copy-to-clipboard/lib/Component";

import Grid from "@material-ui/core/Grid";
import {Button, DialogActions} from "@material-ui/core";
import {DataGrid} from "@mui/x-data-grid";
import EditIcon from "@material-ui/icons/Edit";
import Chip from "@material-ui/core/Chip";

import ExPanel from "./ExPanel";
import FormInput from "./FormInput";
import ErrorMessage from "./ErrorMessage";
import Progress from "./Progress";
import JsonView from "./JsonView";
import JsonSwitch from "./JsonSwitch";

import useApiRequest from "../hooks/useApiRequest";
import useFields from "../hooks/useFields";
import {formatUT} from "../utils/format";
import EditDynamoDBRecordDialog from "./EditDynamoDBRecordDialog";

const formFields = {
    "indexName":{
        label: 'Index',
        type: 'select',
        data: [
            {id: 'main'},
            {id: 'gsimirror'},
            {id: 'gsi1'},
            {id: 'gsi2'},
            {id: 'gsi3'},
            {id: 'gsi4'},
            {id: 'gsi5'},
            {id: 'gsi6'},
            {id: 'gsi7'},
            {id: 'gsi8'},
            {id: 'gsi9'},
            {id: 'gsi10'},
        ],
        multiple: false,
        initValue: 'main'
    },
    "hashKey":{
        label: 'Hash key value',
        initValue: ''
    },
    "rangeKeyCondition":{
        label: 'Range key condition',
        type: 'select',
        data: [
            {id: '='},
            {id: 'begins_with'},
            {id: '<'},
            {id: '<='},
            {id: '>'},
            {id: '>='},
        ],
        multiple: false,
        initValue: '='
    },
    "rangeKey":{
        label: 'Range key value',
        initValue: ''
    },
    "limit": {
        label: 'Limit',
        type: 'number',
        initValue: 20
    },
    "scanIndexForward":{
        label: 'Scan index forward',
        type: 'switch',
        initValue: false,
    },
    "full":{
        label: 'Get full entity',
        type: 'switch',
        initValue: false,
    }
};

const fieldList = [
    'indexName',
    'hashKey',
    'rangeKeyCondition',
    'rangeKey',
    'limit',
    'scanIndexForward',
    'full'
];

const requestType = 'query-dynamodb'

const QueryResult = ({columns, dynamoDBQueryResult, jsonView, loading}) => {
    if(!dynamoDBQueryResult) {
        return null;
    }
    if (jsonView) {
        return <JsonView data={dynamoDBQueryResult} shouldExpandNode={(keyPath, data, level) => level < 2}/>
    }
    return <div style={{height: 450, width: '100%'}}>
        <DataGrid
            disableSelectionOnClick={true}
            rowHeight={46}
            pageSize={10}
            rowsPerPageOptions={[10]}
            getRowId={(row) => row.hashKey + row.rangeKey}
            rows={dynamoDBQueryResult}
            hideFooterSelectedRowCount={true}
            columns={columns}
            loading={loading}
            density='compact'
        />
    </div>
}

export default function DynamoDBQueryPanel({submitRequest}) {
    const formsData = useSelector(state => state.console.formsData[requestType]);
    const dynamoDBQueryResult = useSelector(state => state.console.dynamoDBQueryResult);

    const [requestId, requestInProgress, requestError, newRequest] = useApiRequest();
    const [fields, handleOnChange] = useFields(formFields, formsData, fieldList);
    const [selectedRow, setSelectedRow] = React.useState('');
    const [openDialog, setOpenDialog] = React.useState(false);

    const [jsonView, setJsonView] = React.useState(false);

    React.useEffect(() => {
        if (!dynamoDBQueryResult || !selectedRow) {
            return
        }
        dynamoDBQueryResult.forEach((item) => {
            if (item.hashKey === selectedRow.hashKey && item.rangeKey === selectedRow.rangeKey) {
                setSelectedRow(item);
            }
        })
    }, [dynamoDBQueryResult, selectedRow]);

    const handleSubmitRequest = () => {
        submitRequest(requestType, fields, newRequest());
    }

    const handleEdit = (row) => {
        setSelectedRow(row);
        setOpenDialog(true);
    }

    const handleEditClose = () => {
        setOpenDialog(false);
    }

    const hashKeyCellRenderer = ({row, value}) => {
        const editedRow = !!selectedRow && selectedRow.hashKey === row.hashKey && selectedRow.rangeKey === row.rangeKey
        const deleteIcon = editedRow ? <EditIcon/> : null;
        const onDelete = editedRow ? () => setSelectedRow(null) : null;
        return <CopyToClipboard text={row.hashKey}><Chip
            label={value}
            size="small"
            color={row.blocked ? "secondary" : "default"}
            onClick={() => {
                handleEdit(row)
            }}
            deleteIcon={deleteIcon}
            onDelete={onDelete}
        /></CopyToClipboard>
    }

    const updatedCellRenderer = ({value}) => {
        if (!value) {
            return null;
        }
        return formatUT(value * 1000)
    }

    const columns = [
        {field: 'hashKey', headerName: 'hashKey', width: 370, renderCell: hashKeyCellRenderer},
        {field: 'rangeKey', headerName: 'rangeKey', width: 400},
        {field: 'aws:rep:updatetime', headerName: 'Updated', width: 300, renderCell: updatedCellRenderer},
    ]

    return (
        <ExPanel title="DynamoDB Query">
            <Grid item xs={12}>
                <form>
                    {fieldList.map((name) => (
                        <FormInput
                            key={name}
                            name={name}
                            onChange={handleOnChange}
                            getEditValue={name => fields[name]}
                            formFields={formFields}
                        />
                    ))}
                </form>
                <DialogActions>
                    <Button disabled={requestInProgress} variant="contained" color="primary" onClick={handleSubmitRequest}>Submit</Button>
                </DialogActions>
                <ErrorMessage errorMessage={requestError}/>
                <Progress show={requestInProgress} requestId={requestId}/>
                <JsonSwitch hide={!dynamoDBQueryResult} jsonView={jsonView} setJsonView={setJsonView}/>
                <QueryResult
                    loading={requestInProgress}
                    jsonView={jsonView}
                    dynamoDBQueryResult={dynamoDBQueryResult}
                    columns={columns}
                />
                {openDialog && <EditDynamoDBRecordDialog
                    data={selectedRow}
                    onClose={handleEditClose}
                    submitRequest={submitRequest}
                    parentRequestId={requestId}
                />}
            </Grid>
        </ExPanel>
    )
}
